import React, { createContext, useContext, useState, ReactNode, useRef, MutableRefObject, useEffect } from "react"
import * as osfiles from "../osfiles"
import { fetchWithToken } from "@/common/fetch"
import { Translates } from "@/services/repository/i18n"

import './files.css'
import { Stream } from "@/services/common/stream"

const FilesContext = createContext<FilesContextType | undefined>(undefined)

export interface File {
    id: string
    url: string
}

export type URLCB = (is: string) => string

export interface FilesContextType {
    files: File[] | undefined
    capturePhoto: () => Promise<void>
    selectFiles: () => Promise<void>
    removeFile: (fileId: string) => Promise<void>
    isPhotoCaptureSupported: boolean
    statusStream: Stream<osfiles.UploadStatus>
    filesId: MutableRefObject<string | undefined>
    isRefreshing: boolean
}

export interface FilesProviderProps {
    children?: ReactNode
    insertUrl: string
    updateUrl: string
    fetchUrl: string
    removeUrl: string
    token?: string
    translates: Translates
    filesId?: string
    onChange?: (id: string) => void
    setUploading?: (status: boolean) => void
    statusStream: Stream<osfiles.UploadStatus>
}


export const FilesProvider: React.FC<FilesProviderProps> = (props) => {
    const { children, insertUrl, fetchUrl, removeUrl, translates, updateUrl, token, onChange, setUploading } = props
    const [files, setFiles] = useState<File[] | undefined>(undefined)
    const filesId = useRef<string | undefined>(props.filesId)
    const [isRefreshing, setRefreshing] = useState<boolean>(false)
    const statusStreamRef = useRef<Stream<osfiles.UploadStatus>>(props.statusStream || new Stream<osfiles.UploadStatus>())

    const getFilesURL = () => {
        if (filesId.current === undefined) {
            return insertUrl
        }
        return updateUrl.replace('{id}', filesId.current || '')
    }

    const refresh = async () => {
        if (filesId.current === undefined) {
            setFiles([])
            return
        }
        setRefreshing(true)
        const url = fetchUrl.replace('{id}', filesId.current)
        return fetchWithToken(url, {
            token: token
        }).then(async (resp) => {
            if (!resp.ok) {
                console.error(`${url} fetch data error`)
                return
            }
            const data = await resp.json()
            setFiles(data)
            if (onChange && filesId.current) {
                onChange(filesId.current)
            }
        }).then(() => setRefreshing(false), () => setRefreshing(false))
    }

    const afterUpload = (data: osfiles.CreateFilesResponse | undefined) => {
        if (filesId.current === undefined) {
            if (data?.id) {
                filesId.current = data.id
            } else {
                throw new Error('Expected id returned by response when create files')
            }
        }
        if (setUploading) {
            setUploading(false)
        }
        refresh()
    }

    const selectFiles = async () => {
        osfiles.selectFiles().then(async (files) => {
            if (files === null || files.length === 0) {
                return
            }
            if (setUploading) {
                setUploading(true)
            }
            return osfiles.uploadFiles({
                url: getFilesURL(),
                urlToken: token,
                files: files,
                onSuccess: afterUpload,
                onFail: () => setUploading && setUploading(false),
                translates,
                statusStream: statusStreamRef.current,
            })
        })
    }

    const capturePhoto = async () => {
        osfiles.takePhoto().then(async (files) => {
            if (files === null || files.length === 0) {
                return
            }
            if (setUploading) {
                setUploading(true)
            }
            return osfiles.uploadFiles({
                url: getFilesURL(),
                urlToken: token,
                files: files,
                onSuccess: afterUpload,
                onFail: () => setUploading && setUploading(false),
                translates,
                statusStream: statusStreamRef.current,
            })
        })
    }

    const removeFile = async (fileId: string): Promise<void> => {
        const url = removeUrl.replace('{id}', filesId.current || '').replace('{fid}', fileId)
        const headers = new Headers();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return fetch(url, {
            method: 'DELETE',
            headers,
        }).then(resp => {
            refresh()
            return
        });
    }

    const isPhotoCaptureSupported = osfiles.isPhotoCaptureSupported()

    useEffect(() => {
        if (filesId === undefined) {
            return
        }
        refresh()
    }, [])

    return (
        <FilesContext.Provider value={{ files, isPhotoCaptureSupported, capturePhoto, selectFiles, statusStream: statusStreamRef.current, filesId, isRefreshing, removeFile }}>
            {children}
        </FilesContext.Provider>
    )
}

export const useFiles = (): FilesContextType => {
    const context = useContext(FilesContext)
    if (!context) {
        throw new Error("useFiles must be used within a FilesProvider")
    }
    return context
}

export const SimpleFiles = (props: { readOnly?: boolean, className?: string }) => {
    return <div className={`files-container ${props.className || 'simple-files-container'}`}>
        <SimpleFilesFiles>
            {props.readOnly ? null : <UploadButton />}
            {props.readOnly ? null : <CaptureButton />}
        </SimpleFilesFiles>
        <StatusBar />
    </div>
}

const FileWithRemove = ({ name, remove }: { name: string, remove: () => void }) => {
    return <div className='file-row'>
        <div className="file-name">{name}</div>
        <div className="remove-box" onClick={remove}>
            <i className='svgfont-trash' />
        </div>
    </div>
}

export const SimpleFilesFiles = ({ children }: { children: ReactNode }) => {
    const { files, statusStream, isRefreshing, removeFile } = useFiles()
    const [status, setStatus] = useState<osfiles.UploadStatus>()

    useEffect(() => {
        const id = statusStream.listen((status) => {
            setStatus(status)
        })
        return () => {
            statusStream.remove(id)
        }
    })

    if (isRefreshing || status?.started) {
        return <div className="spinner"></div>
    }
    return <div className="files-list">
        {files ? files.map((row, i) => <FileWithRemove key={i} name={row.id} remove={() => {
            removeFile(row.id)
        }} />) : null}
        {children}
    </div>
}

export const UploadButton = () => {
    const { selectFiles } = useFiles()
    return <div className="files-button" onClick={() => selectFiles()}>
        <i className='svgfont-files-upload' />
    </div>
}

export const CaptureButton = () => {
    const { capturePhoto, isPhotoCaptureSupported } = useFiles()
    if (!isPhotoCaptureSupported) {
        return null
    }
    return <div className="files-button" onClick={() => capturePhoto()}>
        <i className='svgfont-files-camera' />
    </div>
}

export const StatusBar = () => {
    const { statusStream } = useFiles()
    const [status, setStatus] = useState<osfiles.UploadStatus>()

    useEffect(() => {
        const id = statusStream.listen((status) => {
            setStatus(status)
        })
        return () => {
            statusStream.remove(id)
        }
    })

    if (status === undefined) {
        return null
    }
    return <div className="files-status">
        {status.progress > 0 && (
            <div className="progress-container">
                <div
                    className="progress-bar"
                    style={{
                        width: `${status.progress}%`,
                    }}
                />
                <p className="progress-text">{status.progress}%</p>
            </div>
        )}
        {status.message && (
            <p className={`message ${status.error ? "message-error" : "message-success"}`} >
                {status.message}
            </p>
        )}
    </div>
}
